const en = {
  meta: {
    siteName: "Nieder-Eschbacher Pflegedienst GmbH",
    titleSuffix: "Nieder-Eschbacher Pflegedienst",
    description:
      "Nieder-Eschbacher Pflegedienst GmbH, home care service in Frankfurt am Main. Attentive, skilled care at home: personal care, medical nursing care, household help and 24-hour availability.",
    pages: {
      home: "",
      about: "About us",
      services: "Services",
      news: "News & Jobs",
      contact: "Contact",
      imprint: "Legal notice",
      privacy: "Privacy policy",
      notFound: "Page not found",
    },
  },

  common: {
    call: "Call",
    callNow: "Call now:",
    contactUs: "Get in touch",
    bookConsultation: "Book a consultation",
    bookAppointment: "Book an appointment",
    requestConsultation: "Request a consultation",
    aroundTheClock: "Around the clock:",
    reachable24h: "Available around the clock",
    certifiedStaff: "Qualified nursing staff",
    whatsapp: "WhatsApp",
    whatsappText: "Hello, I have a question about your care service.",
    whatsappNote: "Please do not send health data via WhatsApp. In an emergency, please call us.",
    symbolImage: "Stock photo",
    language: "Language",
    changeLanguage: "Choose language",
    skipToContent: "Skip to content",
    legalNote: "This translation is provided for information only. The German version is legally binding.",
  },

  header: {
    topbar24h: "Available 24 hours",
    topbarConsult: "Free, personal consultation",
    mainNav: "Main navigation",
    mobileNav: "Mobile navigation",
    openMenu: "Open menu",
    closeMenu: "Close menu",
  },

  nav: {
    home: "Home",
    about: "About us",
    services: "Services",
    news: "News",
    contact: "Contact",
    jobs: "Jobs",
    imprint: "Legal notice",
    privacy: "Privacy",
  },

  footer: {
    reach: "How to reach us",
    fax: "Fax:",
    emergency: "24-hour emergency line:",
    navigation: "Navigation",
    legal: "Legal",
    area: "Frankfurt am Main and surroundings (focus on Nieder-Eschbach)",
    rights: "All rights reserved.",
  },

  floating: {
    whatsappLabel: "Message us on WhatsApp",
    callLabel: "Call now:",
    toTop: "Scroll to top",
  },

  home: {
    eyebrow: "Home care service · Frankfurt am Main",
    h1a: "Attentive, skilled",
    h1b: "care at home",
    lead:
      "For a life free of worry, even when age or illness brings the need for care. We take time for you.",
    heroImgAlt: "A Nieder-Eschbacher Pflegedienst carer rests her hands on the shoulders of a smiling elderly man, next to the logo and services: basic care, medical care, household help and individual support",
    badgeTitle: "With heart and time",
    badgeText: "Care that does not rush",
    welcomeQuote:
      "We, the team of Nieder-Eschbacher Pflegedienst in Frankfurt am Main, are glad you found us. Day after day we stand up for the people who place their trust in us, providing high-quality care in the privacy of your own home. Our friendly nursing staff take time for you.",
    features: [
      {
        icon: "Clock",
        title: "We take our time",
        text: "Caring, unhurried support. We want you to feel at ease with us.",
        to: "about",
        cta: "About us",
      },
      {
        icon: "HeartHandshake",
        title: "Our services",
        text: "Personal and medical nursing care, tailored to you.",
        to: "services",
        cta: "Services",
      },
      {
        icon: "Newspaper",
        title: "News & jobs",
        text: "Updates from our service and open positions in care.",
        to: "news",
        cta: "News",
      },
    ],
    servicesEyebrow: "Our services",
    servicesTitle: "Individual care, shaped around your needs",
    servicesLead:
      "From personal care to 24-hour availability: we support you comprehensively in the home you know.",
    valuesEyebrow: "Our values",
    valuesTitle: "Respect, care and a responsible approach",
    valuesLead:
      "Respect, care and a responsible approach are our guiding principles. We treat every person the way we would want to be treated ourselves.",
    ctaTitle: "Unsure which care level applies to you?",
    ctaLead: "We are happy to advise you in person, at our office or in your home. Free of charge and without obligation.",
  },

  values: [
    { icon: "Heart", label: "Individual care" },
    { icon: "BadgeCheck", label: "Qualified nursing staff" },
    { icon: "MessageCircle", label: "Personal advice" },
    { icon: "ShieldCheck", label: "Responsible approach" },
  ],

  services: [
    {
      id: "grundpflege",
      icon: "HandHeart",
      title: "Personal care",
      short: "Washing, mobility and everyday support, calmly and with dignity.",
      description:
        "In personal care we support you sensitively with all daily routines of hygiene and mobility. We take our time and always respect your dignity and your right to decide for yourself.",
      points: [
        "Personal hygiene (washing, bathing, showering)",
        "Mobilisation and positioning",
        "Incontinence care",
        "Help with eating and drinking",
        "Dressing and undressing",
      ],
    },
    {
      id: "behandlungspflege",
      icon: "Stethoscope",
      title: "Medical nursing care",
      short: "Medical care as prescribed by your doctor, reliable and professional.",
      description:
        "Medical nursing care covers all medically necessary measures and is always carried out on a doctor's orders. Our qualified nurses work closely with your doctors.",
      points: [
        "Administering and preparing medication",
        "Injections",
        "Putting on and removing compression stockings",
        "Modern wound care",
        "Blood sugar and blood pressure checks",
      ],
      note: "Medical nursing care is always provided on a doctor's orders.",
    },
    {
      id: "hauswirtschaft",
      icon: "Home",
      title: "Household help",
      short: "We look after the household so that you feel completely at home.",
      description:
        "A well-kept home matters for your well-being. We help you run the household so that you can feel relaxed and secure within your own four walls.",
      points: [
        "Shopping and errands",
        "Preparing meals",
        "Laundry",
        "Cleaning the home",
      ],
    },
    {
      id: "betreuung",
      icon: "Users",
      title: "Additional support services",
      short: "Shared time, conversation and activities for more joy in life.",
      description:
        "Alongside nursing care, human warmth matters a great deal to us. We relieve family carers and give you time for pleasant moments together.",
      points: [
        "Relief for family carers",
        "Orientation training",
        "Conversation and company",
        "Biography work",
      ],
    },
    {
      id: "anleitung",
      icon: "GraduationCap",
      title: "Guidance for family carers",
      short: "We help your relatives to help you.",
      description:
        "Family carers do remarkable work. We give practical guidance and confidence in providing care, individually and in plain terms, in your own home.",
      points: [
        "Practical care instruction at home",
        "Advice on care aids",
        "Tips for everyday life with care needs",
        "Confidence in handling care",
      ],
    },
    {
      id: "erreichbarkeit",
      icon: "Clock",
      title: "24-hour availability",
      short: "Reachable on our mobile line around the clock, so you can feel safe.",
      description:
        "In an emergency we are there for you at any time of day or night. Our mobile emergency number reaches us around the clock.",
      points: [
        "Mobile emergency line: 0177 / 7202956",
        "Alternative: 0157 / 36442746",
        "Fast help when it matters",
        "Security for you and your family",
      ],
    },
  ],

  servicesPage: {
    eyebrow: "Services",
    title: "Comprehensive care, individually for you",
    subtitle:
      "We offer a wide range of nursing and household services, matched precisely to your situation.",
    shortLongTitle: "Short-term and long-term care",
    shortLongText:
      "Whether temporarily after a hospital stay or on a permanent basis: we adapt our care flexibly to your circumstances, so you receive exactly the support you need right now.",
    adviceTitle: "Advice on care levels and care insurance",
    adviceText:
      "The care system is complex. We explain in plain terms which benefits you are entitled to and support you step by step with your application to the care insurance fund.",
    ctaTitle: "Request a free consultation",
    ctaLead: "We are happy to advise you in person and without obligation, at our office or in your home.",
  },

  about: {
    eyebrow: "About us",
    title: "People who take their time",
    subtitle:
      "Get to know our team and our philosophy. Time, respect, care and genuine humanity define us.",
    introTitle: "Your well-being comes first",
    intro: [
      "We are the team of Nieder-Eschbacher Pflegedienst in Frankfurt am Main. Day after day we stand up for the people who place their trust in us. To us, care is far more than a job. It is a matter of the heart.",
      "Our friendly, qualified nurses deliberately take time for you, because good care needs calm, attention and a listening ear, without rush.",
      "We care for you where you feel most at ease: in your own home, in familiar surroundings. That way you keep your independence and your privacy.",
    ],
    teamImgAlt: "A carer gently holds the hands of an elderly woman in a wheelchair (stock photo)",
    valuesTitle: "The values that guide us",
    valuesLead: "Respect, care and a responsible approach are our guiding principles.",
    pillars: [
      { icon: "Clock", title: "Time", text: "We work without rush and give you our full attention." },
      { icon: "Heart", title: "Care", text: "Every person is unique. So is the care we provide." },
      { icon: "Home", title: "Home", text: "Care in familiar surroundings, where you feel safe." },
    ],
    ctaTitle: "Get to know us",
    ctaLead: "We look forward to a personal conversation with you.",
  },

  newsPage: {
    eyebrow: "News",
    title: "News & jobs",
    subtitle: "Updates from our care service and open positions for everyone who would like to join our team.",
    newsTitle: "News",
    newsLead: "Current information from our care service.",
    careerEyebrow: "Careers",
    careerTitle: "Join our team",
    careerLead:
      "You love nursing and want to work in a warm-hearted team that has real time for people? Then we look forward to your application.",
    applyNow: "Apply now",
    applySubject: "Application: Care assistant (m/f/d)",
    tasks: "Your tasks",
    profile: "Your profile",
    offer: "What we offer",
    interested: "Interested? Send your application to",
    orCall: "or call us on",
    questions: "Questions? Contact us",
  },

  news: [
    {
      id: "willkommen",
      date: "2026-09-21",
      category: "News",
      title: "Welcome to our new website",
      excerpt:
        "We are pleased to present our care service and our services online from now on: clear, well organised and always available.",
      body: "Our aim is to make all important information about care at home easily accessible to you and your family. If you have questions, you can reach us at any time by phone or through our contact form.",
    },
    {
      id: "pflegeberatung",
      date: "2025-10-28",
      category: "Advice",
      title: "Free care consultation at our office or in your home",
      excerpt:
        "Unsure which care level suits you? We are happy to advise you in person and without obligation on all questions about care insurance and care levels.",
      body: "The German care system is complex. We take time to explain your options calmly and support you in applying for benefits. Simply make an appointment.",
    },
    {
      id: "team-verstaerkung",
      date: "2025-10-05",
      category: "Team",
      title: "We are growing and looking for new colleagues",
      excerpt:
        "Our team keeps growing. Become part of a warm-hearted group of colleagues who practise care with time and respect.",
      body: "We offer fair working conditions, an appreciative atmosphere and real time for the people we care for. Have a look at our current job openings.",
    },
  ],

  jobs: [
    {
      id: "pflegehelfer",
      title: "Care assistant (m/f/d)",
      type: "Part-time or full-time",
      location: "Frankfurt am Main, Nieder-Eschbach",
      intro:
        "To strengthen our team we are looking for a care assistant who brings our values of time, respect and care to life. Career changers with heart are welcome too.",
      tasks: [
        "Support with the personal care of our clients",
        "Help with mobility and everyday tasks",
        "Companionship and human warmth",
        "Close cooperation within the nursing team",
      ],
      requirements: [
        "Enjoyment of working with people and empathy",
        "Reliability, sense of responsibility and team spirit",
        "First experience in care is an advantage (not a must)",
        "Category B driving licence is an advantage",
      ],
      offer: [
        "Fair pay, always on time",
        "An appreciative, warm-hearted team",
        "Real time for care, without rush",
        "Onboarding plus further training",
      ],
    },
  ],

  contact: {
    eyebrow: "Contact",
    title: "We are here for you",
    subtitle:
      "Questions about care, an appointment or advice: get in touch without obligation. We look forward to hearing from you.",
    formTitle: "Write to us",
    formLead: "We will get back to you as soon as possible.",
    name: "Name",
    namePlaceholder: "Your name",
    email: "Email",
    emailPlaceholder: "name@example.com",
    phone: "Phone",
    phonePlaceholder: "Your phone number",
    message: "Message",
    messagePlaceholder: "How can we help you?",
    consentBefore: "I have read the",
    consentLink: "privacy policy",
    consentAfter:
      "and consent to my details (including any health-related information in my message) being processed to handle my enquiry. I can withdraw this consent at any time.",
    submit: "Send message",
    sending: "Sending…",
    subject: "Enquiry via website",
    errName: "Please enter your name.",
    errEmail: "Please enter a valid email address.",
    errMessage: "Please write us a short message.",
    errConsent: "Please agree to the privacy policy.",
    toastCheck: "Please check your entries.",
    toastSuccess: "Thank you! Your message has been sent.",
    toastError: "Something went wrong. Please give us a call.",
    successTitle: "Thank you!",
    successText: "Your message has been sent. We will get back to you shortly.",
    sendAnother: "Send another message",
    reachTitle: "How to reach us",
    address: "Address",
    phoneLabel: "Phone",
    fax: "Fax",
    emergency: "24-hour emergency line",
    alternatively: "alternatively",
    emailLabel: "Email",
    hoursTitle: "Office hours",
    hours: "Monday to Friday: 8:00 am to 5:00 pm",
    careAndEmergency: "Nursing care and emergency line:",
    aroundTheClock: "around the clock",
    mapTitle: "How to find us",
    mapIframeTitle: "Location of Nieder-Eschbacher Pflegedienst on Google Maps",
    mapLoad: "Load map",
    mapNotice:
      "For privacy reasons the Google Maps map is only loaded after your consent. Data is then transferred to Google.",
    mapButton: "Load map and show location",
    mapAlt: "Or open in Google Maps",
  },

  reviews: {
    eyebrow: "Reviews",
    title: "What families and patients say",
    emptyLead: "Your experience helps other families in Frankfurt find the right care service.",
    countOn: "reviews on",
    source: "Public reviews from our Google Business Profile.",
    asOf: "As of",
    all: "All reviews on Google",
    write: "Write a review",
    on: "on",
    starsLabel: "out of 5 stars",
  },

  notFound: {
    title: "Page not found",
    subtitle: "Error 404",
    text: "The page you requested does not exist or has been moved.",
    home: "Back to the home page",
  },

  imprint: {
    title: "Legal notice",
    subtitle: "Information pursuant to Section 5 of the German Digital Services Act (DDG)",
    company: "Information pursuant to Section 5 DDG",
    country: "Germany",
    representedBy: "Represented by",
    managingDirector: "Managing director",
    contact: "Contact",
    phone: "Phone",
    fax: "Fax",
    email: "Email",
    register: "Commercial register",
    registerCourt: "Register court",
    registerNumber: "Registration number",
    taxNumber: "Tax number",
    licence: "Licence and supervisory authority",
    licenceText:
      "Licensed home care service under a care agreement pursuant to Section 72 of the German Social Code, Book XI (SGB XI), with the regional associations of the long-term care insurance funds in Hesse.",
    authorityText:
      "Competent supervisory authority under the Hessian Act on Support and Care Services (HGBP):",
    dispute: "Consumer dispute resolution",
    disputeText:
      "We are neither willing nor obliged to take part in dispute resolution proceedings before a consumer arbitration board (Section 36 VSBG).",
    responsible: "Responsible for content pursuant to Section 18 (2) of the German State Media Treaty (MStV)",
    credit: "Concept and technical implementation",
    liabilityContent: "Liability for content",
    liabilityContentText:
      "As a service provider we are responsible for our own content on these pages under general law. We are not, however, obliged to monitor transmitted or stored third-party information or to investigate circumstances that indicate unlawful activity (Article 8 of Regulation (EU) 2022/2065, the Digital Services Act). Obligations to remove or block the use of information under general law remain unaffected. Liability in this respect is only possible from the moment we become aware of a specific infringement. As soon as we become aware of such infringements, we will remove the content concerned without delay.",
    liabilityLinks: "Liability for links",
    liabilityLinksText:
      "Our website contains links to external third-party websites whose content we cannot influence. We therefore cannot accept any responsibility for that third-party content. The respective provider or operator is always responsible for the content of linked pages. The linked pages were checked for possible legal violations at the time of linking; unlawful content was not apparent at that time. Permanent monitoring of the content of linked pages is not reasonable without concrete indications of an infringement. As soon as we become aware of infringements, we will remove such links without delay.",
    copyright: "Copyright",
    copyrightText:
      "The content and works created by the site operator on these pages are subject to German copyright law. Reproduction, editing, distribution and any kind of use beyond the limits of copyright law require the written consent of the respective author or creator. Downloads and copies of this site are permitted for private, non-commercial use only. Where content on this site was not created by the operator, the copyrights of third parties are respected; in particular, third-party content is marked as such. Should you nevertheless become aware of a copyright infringement, please let us know. As soon as we become aware of infringements, we will remove such content without delay.",
  },

  privacy: {
    title: "Privacy policy",
    subtitle: "Information on the processing of your personal data under the GDPR",
    retention: "six months",
    lastUpdated: "Last updated: October 2026",
    controller: {
      title: "1. Controller",
      lead: "The controller responsible for data processing on this website within the meaning of the General Data Protection Regulation (GDPR) is:",
      representedBy: "represented by its managing director",
      phone: "Phone",
      fax: "Fax",
      email: "Email",
    },
    dpo: {
      title: "Data protection officer",
      lead: "You can reach our data protection officer at:",
    },
    general: {
      title: "2. General information on data processing",
      p: [
        "We process personal data only to the extent necessary to provide a functioning website and to handle your enquiries. The legal bases are Article 6(1)(a) GDPR (consent), Article 6(1)(b) GDPR (contract or pre-contractual steps) and Article 6(1)(f) GDPR (legitimate interest in the secure and stable operation of this website).",
        "This website sets **no cookies**, stores no information on your device (Section 25 TDDDG) and uses no analytics, tracking or advertising services. Fonts and images are delivered from our own web server; no data is sent to third-party font or image services when you open our pages. There is no automated decision-making, including profiling (Article 22 GDPR).",
      ],
    },
    hosting: {
      title: "3. Hosting and server log files",
      ownIntro:
        "This website is operated on our behalf by {operator} on a server in Germany. The data centre and server provider is {provider}.",
      logs: "Each time you access the site, technically necessary connection data is processed: IP address, date and time of access, page requested, amount of data transferred, browser type and version, operating system and the page visited before (referrer). This data is not combined with other data sources and serves solely to deliver the website, to keep it secure and to analyse errors. The legal basis is Article 6(1)(f) GDPR. Server log files are stored only for as long as secure operation requires and are then deleted or anonymised.",
      ownLegal:
        "Data processing agreements under Article 28 GDPR are in place with the operator and the data centre provider. Hosting involves no transfer to third countries.",
    },
    form: {
      title: "4. Contact form",
      p: [
        "If you send us an enquiry through the contact form, we process the details you enter (name, email address, optionally your phone number, and your message) to handle your enquiry and any follow-up questions. The legal basis is Article 6(1)(b) GDPR (initiation of a care relationship) and, where you share health-related details with us, your explicit consent under Article 9(2)(a) GDPR, which you give by ticking the box in the form.",
        "Name, email address and message are mandatory; the phone number is optional. Without the mandatory details we cannot process your enquiry. You can always reach us by phone instead. The data is transmitted in encrypted form (TLS). Your enquiry is stored exclusively on a server commissioned by us in Germany and is not passed on to third parties. We delete the data no later than {retention} after your enquiry has been dealt with, unless a care relationship is established or statutory retention obligations apply. You can withdraw your consent at any time with effect for the future, for example by emailing {email}.",
      ],
    },
    emailPhone: {
      title: "5. Contact by email, phone or WhatsApp",
      p: [
        "If you contact us by email or phone, we process your details to deal with your request (Article 6(1)(b) and (f) GDPR). Please note that unencrypted emails can be read by third parties in transit; for health-related details we recommend the phone or a personal conversation.",
        "Email to {email} reaches us through a forwarding service: the relay is {forwarder} and the mailbox is held at {mailbox}. Your sender address, subject line and message content are processed in the course of this and are also transferred to the USA. The legal basis is Article 6(1)(f) GDPR. Please therefore do not send health data by email and use the phone or a personal conversation instead.",
        "Our website contains a link that lets you message us on WhatsApp. The provider is WhatsApp Ireland Limited, 4 Grand Canal Square, Grand Canal Harbour, Dublin 2, Ireland. The link is a plain reference: only when you click it is a connection to WhatsApp established and your mobile number and the content of your message transmitted to WhatsApp. The legal basis is Article 6(1)(b) GDPR. WhatsApp also processes data outside the EU; Meta is certified under the EU-US Data Privacy Framework. Please do not send health data via WhatsApp, and use the phone in an emergency.",
      ],
    },
    applications: {
      title: "6. Job applications",
      p: [
        "Applications for our job openings are sent to us by email. We process your application documents solely to carry out the application procedure (Article 6(1)(b) GDPR, Section 26 BDSG). After the procedure has ended, the documents are deleted no later than six months afterwards, unless you have consented to longer storage or an employment relationship is established.",
      ],
    },
    maps: {
      title: "7. Google Maps (two-click solution)",
      p: [
        "On the contact page we offer a map from the Google Maps service. The provider is Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Ireland. The map is loaded **only after you explicitly click** “Load map”; before that no data is sent to Google. By clicking you consent (Article 6(1)(a) GDPR) to Google processing your IP address and usage data and, where applicable, transferring it to servers of Google LLC in the USA; Google may set cookies on your device in the process. Google is certified under the EU-US Data Privacy Framework and additionally uses standard contractual clauses. Your consent applies to the current page view only. Further information: [policies.google.com/privacy](https://policies.google.com/privacy).",
      ],
    },
    rights: {
      title: "8. Your rights",
      lead: "With regard to your personal data you have the following rights against us:",
      items: [
        "Access (Article 15 GDPR)",
        "Rectification (Article 16 GDPR)",
        "Erasure (Article 17 GDPR)",
        "Restriction of processing (Article 18 GDPR)",
        "Data portability (Article 20 GDPR)",
        "Objection to processing based on Article 6(1)(f) GDPR (Article 21 GDPR)",
        "Withdrawal of consent with effect for the future (Article 7(3) GDPR)",
      ],
      how: "To exercise your rights, an informal message to {email} or by post to the address above is sufficient.",
    },
    authority: {
      title: "9. Right to lodge a complaint with a supervisory authority",
      lead: "You have the right to lodge a complaint with a data protection supervisory authority (Article 77 GDPR). The authority responsible for us is:",
      name: "The Hessian Commissioner for Data Protection and Freedom of Information",
      address: "Gustav-Stresemann-Ring 1, 65189 Wiesbaden, Germany",
      url: "https://datenschutz.hessen.de",
      urlLabel: "datenschutz.hessen.de",
    },
    security: {
      title: "10. Data security",
      p: [
        "This website is delivered exclusively over an encrypted HTTPS connection (TLS). We use technical and organisational measures to protect your data against loss, manipulation and unauthorised access, and we keep these measures up to date with the state of the art.",
      ],
    },
    changes: {
      title: "11. Changes to this privacy policy",
      p: [
        "We update this privacy policy whenever the legal situation or our website changes. The version published here applies.",
      ],
    },
  },
};

export default en;
